/**
 * useActiveSection — return the id of the topmost on-screen section.
 *
 * Drives the sticky SectionJumpBar (Slice U-F3b) so the active chip tracks
 * which section the reader is actually looking at. Backed by a single
 * IntersectionObserver wired to the section elements identified by `ids`.
 *
 * "Topmost" = smallest positive `getBoundingClientRect().top` among entries
 * currently intersecting. Sections above the viewport (negative top) are
 * skipped when a below-the-fold-edge candidate exists, which keeps the
 * highlight on the section the user is *reading* rather than one they have
 * already scrolled past.
 *
 * The rootMargin shrinks the effective viewport by 60px at the top (matches
 * the sticky bar height) and by 40% at the bottom so a section has to be
 * meaningfully into view — not just peeking — before it becomes "active".
 *
 * The observer is recreated whenever the *set* of ids changes (joined into
 * a stable string for the effect dep). Re-renders that pass an equivalent
 * array don't churn the observer.
 *
 * The pure `findTopmost` helper is exported so the unit tests can pin the
 * tie-breaking rules without standing up a DOM (mirrors the pattern in
 * `useOrganizations.ts` → `derivePendingState`).
 */

import { useEffect, useState } from 'react'

/* -------------------------------------------------------------------------- */
/* Pure helper — exported for unit tests                                      */
/* -------------------------------------------------------------------------- */

export interface SectionEntry {
  id: string
  top: number
  intersecting: boolean
}

/**
 * Pick the topmost on-screen section id from a snapshot of entries.
 *
 * Rules:
 *   1. Only `intersecting` entries are eligible.
 *   2. Among those, prefer entries with a non-negative `top` (i.e. at or
 *      below the viewport's effective top edge) and pick the smallest.
 *   3. If every intersecting entry has a negative top (the user is between
 *      sections, or above all of them), fall back to the entry whose top is
 *      closest to zero — i.e. the largest (least-negative) value.
 *   4. Returns `null` when nothing is intersecting.
 */
export function findTopmost(entries: SectionEntry[]): string | null {
  const visible = entries.filter(e => e.intersecting)
  if (visible.length === 0) return null

  const positive = visible.filter(e => e.top >= 0)
  if (positive.length > 0) {
    return positive.reduce((best, e) => (e.top < best.top ? e : best)).id
  }

  // All negatives — pick the one closest to the top edge from above.
  return visible.reduce((best, e) => (e.top > best.top ? e : best)).id
}

/* -------------------------------------------------------------------------- */
/* Hook                                                                       */
/* -------------------------------------------------------------------------- */

const OBSERVER_OPTIONS: IntersectionObserverInit = {
  threshold: 0.3,
  rootMargin: '-60px 0px -40% 0px',
}

export function useActiveSection(ids: string[]): string | null {
  const [activeId, setActiveId] = useState<string | null>(null)
  // Stable dep — re-runs only when the *set* of ids changes, not on every
  // render that passes a freshly-allocated array of the same contents.
  const depKey = ids.join('|')

  useEffect(() => {
    if (ids.length === 0) {
      setActiveId(null)
      return
    }

    // SSR / non-DOM environments: bail. The hook is intended for client
    // components but the codebase doesn't currently gate hooks by `typeof
    // window` — keeping a soft guard here means consumers don't have to.
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      return
    }

    const elements = ids
      .map(id => ({ id, el: document.getElementById(id) }))
      .filter((x): x is { id: string; el: HTMLElement } => x.el !== null)

    if (elements.length === 0) return

    const observer = new IntersectionObserver(observerEntries => {
      // Re-snapshot every tracked element on each callback so we don't
      // confuse a not-currently-fired entry with "no longer intersecting".
      // `getBoundingClientRect` reads layout; this runs only when the
      // browser already fired an intersection event so the cost is bounded.
      const snapshot: SectionEntry[] = elements.map(({ id, el }) => {
        const fired = observerEntries.find(oe => oe.target === el)
        const rect = fired?.boundingClientRect ?? el.getBoundingClientRect()
        const intersecting = fired
          ? fired.isIntersecting
          : isElementIntersecting(rect)
        return { id, top: rect.top, intersecting }
      })

      setActiveId(prev => {
        const next = findTopmost(snapshot)
        return next === prev ? prev : next
      })
    }, OBSERVER_OPTIONS)

    elements.forEach(({ el }) => observer.observe(el))

    return () => observer.disconnect()
    // depKey is the join of ids — captures membership changes without
    // re-running on identity-only array churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depKey])

  return activeId
}

/* -------------------------------------------------------------------------- */
/* Internal helpers                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Best-effort fallback when an element's IntersectionObserverEntry isn't in
 * the current callback batch. Uses a simple viewport-intersection check
 * against the observer's effective bounds (matches `rootMargin` constants).
 */
function isElementIntersecting(rect: DOMRect): boolean {
  const viewportHeight =
    typeof window !== 'undefined' ? window.innerHeight : 0
  const topEdge = 60 // matches rootMargin top: -60px
  const bottomEdge = viewportHeight - viewportHeight * 0.4 // -40% bottom
  return rect.bottom > topEdge && rect.top < bottomEdge
}
